import React from 'react'

// Components
import { Button, LinkButton } from '@sketch/components'

// Local Components
import BillingInfoTable from '../BillingInfoTable'

// Constants
import { routes } from '@sketch/constants'

// Utils
import { formatPriceShort, dateFormat } from '@sketch/utils'
import { PLAN_TYPE_LABEL } from 'modules/workspace/utils'

// GQL
import { BillingPlanFragment } from '@sketch/gql-types'

interface CurrentPlanProps {
  workspaceId: string
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment
  trialEndDate: string
  isSubscriptionEnd: boolean
  onReactivateSubscription: () => void
}

const Header: React.FC = () => (
  <BillingInfoTable.HeadingCell>Current Plan</BillingInfoTable.HeadingCell>
)

export const CurrentPlan = ({
  workspaceId,
  isWorkspaceOnTrial,
  customerHasPaymentDetails,
  currentPlan,
  trialEndDate,
  isSubscriptionEnd,
  onReactivateSubscription,
}: CurrentPlanProps) => {
  /**
   * The workspace is on trial and the customer already configured a following
   * plan
   */
  if (isWorkspaceOnTrial && currentPlan && customerHasPaymentDetails) {
    return (
      <BillingInfoTable.Row>
        <Header />

        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>

        {isSubscriptionEnd && (
          <BillingInfoTable.Cell>
            <Button
              variant="primary"
              size="32"
              onClick={onReactivateSubscription}
            >
              Subscribe Again
            </Button>
          </BillingInfoTable.Cell>
        )}
      </BillingInfoTable.Row>
    )
  } else if (isWorkspaceOnTrial) {
    /**
     * The workspace is only on trial. After that, the workspace will be
     * de-activated
     */
    return (
      <BillingInfoTable.Row>
        <Header />
        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>
        <BillingInfoTable.Cell>
          <LinkButton
            data-testid="setup-subscription-button"
            variant="primary"
            size="32"
            to={routes.WORKSPACE_SUBSCRIBE.create({
              workspaceId,
            })}
          >
            Set Up a Subscription
          </LinkButton>
        </BillingInfoTable.Cell>
      </BillingInfoTable.Row>
    )
  } else {
    /**
     * The workspace is definitely not on trial
     */
    return (
      <BillingInfoTable.Row>
        <Header />

        <BillingInfoTable.Cell>
          <div>
            <strong data-testid="plan-name">
              {PLAN_TYPE_LABEL[currentPlan!.type]}
            </strong>
            <br />
            <span data-testid="plan-price-per-editor">
              <strong>{formatPriceShort(currentPlan!.invoicePrice)}</strong> per
              Seat, per {currentPlan?.type === 'YEARLY' ? 'year' : 'month'}
            </span>
          </div>
        </BillingInfoTable.Cell>
        {isSubscriptionEnd && (
          <BillingInfoTable.Cell>
            <Button
              variant="primary"
              size="32"
              onClick={onReactivateSubscription}
            >
              Subscribe Again
            </Button>
          </BillingInfoTable.Cell>
        )}
      </BillingInfoTable.Row>
    )
  }
}
